import * as THREE from "three";

export const CHUNK_X = 16;
export const CHUNK_Z = 16;
export const WORLD_HEIGHT = 128;
/** Chunks are stored as one tall column, so no sub-chunk splitting. */
export const CHUNK_VOLUME = CHUNK_X * CHUNK_Z * WORLD_HEIGHT;

export const CHUNK_STATE = {
  EMPTY: 0,
  GENERATED: 1,
  MESHED: 2,
} as const;
export type ChunkState = (typeof CHUNK_STATE)[keyof typeof CHUNK_STATE];

export class Chunk {
  /** Chunk-space coordinate (not world voxel coordinate). */
  readonly cx: number;
  readonly cz: number;
  readonly originX: number;
  readonly originZ: number;
  readonly blocks: Uint8Array;
  /** Highest non-air y per column, used to skip empty meshing work. */
  readonly heightMap: Uint8Array;

  state: ChunkState = CHUNK_STATE.EMPTY;
  /** Set when a block changed and the mesh needs rebuilding. */
  dirty = true;
  /** Set when the chunk was generated but could not be meshed (missing neighbours). */
  awaitingNeighbours = false;

  solidMesh: THREE.Mesh | null = null;
  waterMesh: THREE.Mesh | null = null;

  constructor(cx: number, cz: number) {
    this.cx = cx;
    this.cz = cz;
    this.originX = cx * CHUNK_X;
    this.originZ = cz * CHUNK_Z;
    this.blocks = new Uint8Array(CHUNK_VOLUME);
    this.heightMap = new Uint8Array(CHUNK_X * CHUNK_Z);
  }

  /** Index into `blocks`. y-major keeps vertical column walks contiguous. */
  static index(lx: number, y: number, lz: number): number {
    return lx + CHUNK_X * lz + CHUNK_X * CHUNK_Z * y;
  }

  get(lx: number, y: number, lz: number): number {
    return this.blocks[Chunk.index(lx, y, lz)];
  }

  set(lx: number, y: number, lz: number, id: number): void {
    this.blocks[Chunk.index(lx, y, lz)] = id;
  }

  /** Recompute the highest solid y for one column. */
  private recomputeColumnHeight(lx: number, lz: number): void {
    let top = 0;
    for (let y = WORLD_HEIGHT - 1; y >= 0; y--) {
      if (this.blocks[Chunk.index(lx, y, lz)] !== 0) {
        top = y + 1;
        break;
      }
    }
    this.heightMap[lx + CHUNK_X * lz] = top;
  }

  recomputeHeights(): void {
    for (let lz = 0; lz < CHUNK_Z; lz++) {
      for (let lx = 0; lx < CHUNK_X; lx++) this.recomputeColumnHeight(lx, lz);
    }
  }

  recomputeHeight(lx: number, lz: number): void {
    this.recomputeColumnHeight(lx, lz);
  }

  /** Highest non-air y in the column, or 0 if the column is empty. */
  columnHeight(lx: number, lz: number): number {
    return this.heightMap[lx + CHUNK_X * lz];
  }

  dispose(): void {
    if (this.solidMesh) {
      this.solidMesh.geometry.dispose();
      this.solidMesh.removeFromParent();
      this.solidMesh = null;
    }
    if (this.waterMesh) {
      this.waterMesh.geometry.dispose();
      this.waterMesh.removeFromParent();
      this.waterMesh = null;
    }
  }
}

export function chunkKey(cx: number, cz: number): string {
  return `${cx},${cz}`;
}
